/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Fade } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { getContinuousChatNotificationConfig } from 'bundles/ai-coach-platform/components/patterns/chat/notifications/configs/ContinuousChatNotificationConfig';
import { useMemoryPreferenceNotification } from 'bundles/ai-coach-platform/components/patterns/chat/notifications/configs/MemoryPreferenceNotificationConfig';
import type {
  MemoryNotificationConfig,
  NotificationConfig,
} from 'bundles/ai-coach-platform/components/patterns/chat/notifications/types';
import CoachSuggestion from 'bundles/ai-coach-platform/components/patterns/suggestions/CoachSuggestion';
import CoachSuggestionContent from 'bundles/ai-coach-platform/components/patterns/suggestions/CoachSuggestionContent';
import CoachSuggestionFooter from 'bundles/ai-coach-platform/components/patterns/suggestions/CoachSuggestionFooter';
import CoachSuggestionHeader from 'bundles/ai-coach-platform/components/patterns/suggestions/CoachSuggestionHeader';
import CoachSuggestionTitle from 'bundles/ai-coach-platform/components/patterns/suggestions/CoachSuggestionTitle';
import CoachSuggestions from 'bundles/ai-coach-platform/components/patterns/suggestions/CoachSuggestions';
import {
  ResolutionStatus,
  type SuggestionAction as SuggestionActionType,
} from 'bundles/ai-coach-platform/components/patterns/suggestions/types';

import _t from 'i18n!nls/ai-coach-platform';

const styles = {
  root: css`
    background-color: var(--cds-color-neutral-background-primary);
    border-radius: var(--cds-border-radius-200);
    box-shadow: 0 6px 24px 4px var(--cds-color-darken-100);
    max-height: 400px;
    max-width: 600px;
    overflow-y: auto;
    margin: 0 var(--cds-spacing-200) var(--cds-spacing-200);
  `,
};

type Props = {
  onAllResolved?: () => void;
  memoryNotificationConfig?: MemoryNotificationConfig;
  brandLabel?: string;
};

type UseChatNotificationsReturn = {
  notifications: NotificationConfig[];
  handleAction: (notificationId: string, action: SuggestionActionType) => void;
  allResolved: boolean;
};

type UseChatNotificationsParams = {
  onAllResolved?: () => void;
  memoryNotificationConfig?: MemoryNotificationConfig;
  brandLabel?: string;
};

const useChatNotifications = ({
  onAllResolved,
  memoryNotificationConfig,
  brandLabel,
}: UseChatNotificationsParams): UseChatNotificationsReturn => {
  const [notifications, setNotifications] = React.useState<NotificationConfig[]>([]);
  const initializedRef = React.useRef(false);
  const [allResolved, setAllResolved] = React.useState(false);
  // this notification needs a hook since it has a mutation
  const memoryNotification = useMemoryPreferenceNotification(memoryNotificationConfig, brandLabel);

  const track = useTracker();

  const handleAction = React.useCallback<(notificationId: string, action: SuggestionActionType) => void>(
    (notificationId, action) => {
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) => {
          if (notification.id === notificationId) {
            const newStatus = action === 'accept' ? ResolutionStatus.ACCEPTED : ResolutionStatus.REJECTED;
            return {
              ...notification,
              status: newStatus,
            };
          }
          return notification;
        })
      );
    },
    []
  );

  React.useEffect(() => {
    // Only initialize notifications once on mount
    if (!initializedRef.current) {
      initializedRef.current = true;

      // Initialize notifications from imported configurations,
      // filter out those that are dismissed already
      const allNotifications = [memoryNotification, getContinuousChatNotificationConfig()].filter(
        (notification) => !notification.isDismissed
      );

      setNotifications(allNotifications);

      // Call onMount for notifications that have this callback
      allNotifications.forEach((notification) => {
        if (notification.onMount) {
          // Call the mutation when the notification is mounted
          notification.onMount();

          track('view_notification', {
            notification: {
              templateId: notification.id,
            },
          });
        }
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Only run once on mount to avoid infinite loop

  React.useEffect(() => {
    const allNotifsResolved = notifications.every(
      (notification) => notification.status !== ResolutionStatus.UNRESOLVED
    );
    let timeout: NodeJS.Timeout;

    if (allNotifsResolved && notifications.length > 0) {
      // delay resetting so learners can see the resolved state for a bit
      timeout = setTimeout(() => {
        setAllResolved(true);
        onAllResolved?.();
      }, 500);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [notifications, onAllResolved]);

  return {
    notifications,
    handleAction,
    allResolved,
  };
};

export const ChatNotifications = (props: Props): React.ReactElement | null => {
  const { onAllResolved, memoryNotificationConfig, brandLabel } = props;
  const { notifications, handleAction, allResolved } = useChatNotifications({
    onAllResolved,
    memoryNotificationConfig,
    brandLabel,
  });
  const [expandedId, setExpandedId] = React.useState<string | undefined>(undefined);
  const track = useTracker();

  const handleNotificationAction = React.useCallback<
    (notification: NotificationConfig, action: SuggestionActionType) => void
  >(
    (notification, action) => {
      handleAction(notification.id, action);
      notification.onAction?.(action);

      track('click_notification', {
        notification: {
          templateId: notification.id,
        },
      });
    },
    [handleAction, track]
  );

  if (notifications.length === 0) {
    return null;
  }

  return (
    <Fade in={!allResolved} timeout={300}>
      <div className="coach-chat-notifications" css={styles.root}>
        <CoachSuggestions
          ariaLabel={_t('Coach Notifications')}
          suggestionsHeaderLabel={_t('Notifications')}
          expandedId={expandedId}
        >
          {notifications.map((notification) => (
            <CoachSuggestion
              key={notification.id}
              id={notification.id}
              status={notification.status}
              onAction={(action) => handleNotificationAction(notification, action)}
              onToggle={(expanded) => setExpandedId(expanded ? notification.id : undefined)}
            >
              <CoachSuggestionHeader>
                <CoachSuggestionTitle>{notification.title}</CoachSuggestionTitle>
              </CoachSuggestionHeader>
              <CoachSuggestionContent>{notification.content}</CoachSuggestionContent>
              <CoachSuggestionFooter
                acceptLabel={notification.acceptLabel}
                dismissLabel={notification.dismissLabel}
                hideDismiss={notification.hideDismiss}
              />
            </CoachSuggestion>
          ))}
        </CoachSuggestions>
      </div>
    </Fade>
  );
};

export default ChatNotifications;
